import React, { useState, useEffect } from 'react';
import { 
  DollarSign, Clock, CheckCircle, XCircle, 
  AlertCircle, Search, Filter, Calendar,
  User, FileText, MessageSquare, TrendingUp
} from 'lucide-react';
import { commissionsService, agencyService, commissionMessagesService } from '../../services';
import AdminCommissionMessagingModal from './AdminCommissionMessagingModal';
import './CommissionsManagement.css';

const CommissionsManagement = () => {
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCommission, setSelectedCommission] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showMessagingModal, setShowMessagingModal] = useState(false);
  const [selectedCommissionForMessaging, setSelectedCommissionForMessaging] = useState(null);
  const [filters, setFilters] = useState({
    status: 'all',
    agent: 'all',
    dateRange: 'all',
    search: ''
  });
  const [stats, setStats] = useState({
    pending: 0,
    approved: 0,
    denied: 0,
    onHold: 0,
    needsReply: 0,
    totalAmount: 0,
    avgCommission: 0
  });
  const [agents, setAgents] = useState([]);
  const [messageSent, setMessageSent] = useState(false);

  useEffect(() => {
    loadCommissions();
    loadAgents();
  }, []);

  const loadAgents = async () => {
    try {
      const data = await agencyService.getAgents();
      setAgents(data.agents || []);
    } catch (error) {
      console.error('Failed to load agents:', error);
    }
  };

  const loadCommissions = async () => {
    try {
      setLoading(true);
      // Use agency endpoint for agency-wide commission view
      const data = await agencyService.getCommissions({
        status: filters.status !== 'all' ? filters.status : undefined,
        agentId: filters.agent !== 'all' ? filters.agent : undefined,
        dateRange: filters.dateRange !== 'all' ? filters.dateRange : undefined
      });
      
      setCommissions(data.commissions || []);
      
      // Stats are returned by the API
      if (data.stats) {
        setStats({
          pending: data.stats.pending || 0,
          approved: data.stats.approved || 0,
          denied: data.stats.denied || 0,
          onHold: data.stats.onHold || 0,
          needsReply: data.stats.needsReply || 0,
          totalAmount: data.stats.totalAmount || 0,
          avgCommission: data.stats.avgCommission || 0
        });
      }
    } catch (error) {
      console.error('Failed to load commissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (commissionId, status, adminNotes) => {
    try {
      await commissionsService.updateStatus(commissionId, status, adminNotes);
      loadCommissions();
      setShowDetailModal(false);
    } catch (error) {
      console.error('Failed to update commission status:', error);
      alert('Failed to update commission status');
    }
  };

  const handleMessageSent = () => {
    setMessageSent(true);
    loadCommissions();
  };

  // Reload commissions when filters change
  useEffect(() => {
    loadCommissions();
  }, [filters.status, filters.agent, filters.dateRange]);

  const filteredCommissions = commissions.filter(commission => {
    // Search filter only - other filters are handled by the API
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      const fullName = `${commission.first_name} ${commission.last_name}`.toLowerCase();
      const guestName = `${commission.guest_first_name} ${commission.guest_last_name}`.toLowerCase();
      const agentName = `${commission.agent_first_name} ${commission.agent_last_name}`.toLowerCase();
      
      if (!fullName.includes(searchTerm) && 
          !guestName.includes(searchTerm) && 
          !agentName.includes(searchTerm) &&
          !commission.reservation_number?.toLowerCase().includes(searchTerm)) {
        return false;
      }
    }
    
    return true;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'yellow';
      case 'approved': return 'green';
      case 'denied': return 'red';
      case 'on_hold': return 'orange';
      case 'needs_reply': return 'blue';
      default: return 'gray';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return Clock;
      case 'approved': return CheckCircle;
      case 'denied': return XCircle;
      case 'on_hold': return AlertCircle;
      case 'needs_reply': return MessageSquare;
      default: return AlertCircle;
    }
  };

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Commission Management</h1>
        <p className="text-gray-600 mt-2">Review and process agent commission submissions</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Pending Review</p>
              <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
            </div>
            <Clock className="w-8 h-8 text-yellow-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Needs Reply</p>
              <p className="text-2xl font-bold text-blue-600">{stats.needsReply}</p>
            </div>
            <MessageSquare className="w-8 h-8 text-blue-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Approved</p>
              <p className="text-2xl font-bold text-green-600">{stats.approved}</p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Denied</p>
              <p className="text-2xl font-bold text-red-600">{stats.denied}</p>
            </div>
            <XCircle className="w-8 h-8 text-red-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">On Hold</p>
              <p className="text-2xl font-bold text-orange-600">{stats.onHold}</p>
            </div>
            <AlertCircle className="w-8 h-8 text-orange-500" />
          </div>
        </div>
      </div>

      {/* Additional Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Approved Amount</p>
              <p className="text-2xl font-bold text-gray-900">${stats.totalAmount.toLocaleString()}</p>
            </div>
            <DollarSign className="w-8 h-8 text-green-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Average Commission</p>
              <p className="text-2xl font-bold text-gray-900">${stats.avgCommission.toLocaleString()}</p>
            </div>
            <TrendingUp className="w-8 h-8 text-blue-500" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-4 border-b">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search by agent name, guest name, or reservation..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <select
              value={filters.agent}
              onChange={(e) => setFilters({ ...filters, agent: e.target.value })}
              className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Agents</option>
              {agents.map(agent => (
                <option key={agent.id} value={agent.id}>
                  {agent.name || `${agent.first_name} ${agent.last_name}`}
                </option>
              ))}
            </select>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="denied">Denied</option>
              <option value="on_hold">On Hold</option>
              <option value="needs_reply">Needs Reply</option>
            </select>
            <select
              value={filters.dateRange}
              onChange={(e) => setFilters({ ...filters, dateRange: e.target.value })}
              className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="week">Last 7 Days</option>
              <option value="month">Last 30 Days</option>
            </select>
          </div>
        </div>
      </div>

      {/* Commissions Table */}
      <div className="bg-white rounded-lg shadow">
        <div className="overflow-x-auto">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="loading-spinner"></div>
            </div>
          ) : filteredCommissions.length === 0 ? (
            <div className="text-center py-12">
              <DollarSign className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No commissions found</p>
            </div>
          ) : (
            <table className="min-w-full admin-table">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Agent</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Guest</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Booking</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Commission</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Submitted</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredCommissions.map((commission) => {
                  const StatusIcon = getStatusIcon(commission.status);
                  const statusColor = getStatusColor(commission.status);
                  
                  return (
                    <tr key={commission.id}>
                      <td className="px-6 py-4">
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {commission.first_name} {commission.last_name}
                          </p>
                          <p className="text-sm text-gray-500">{commission.email}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-gray-900">
                          {commission.guest_first_name} {commission.guest_last_name}
                        </p>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm">
                          <p className="text-gray-900">{commission.reservation_number}</p>
                          <p className="text-gray-500">{commission.supplier}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm">
                          <p className="font-medium text-gray-900">
                            ${parseFloat(commission.agent_commission || 0).toFixed(2)}
                          </p>
                          <p className="text-gray-500">
                            Gross: ${parseFloat(commission.gross_commission || 0).toFixed(2)}
                          </p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${statusColor}-100 text-${statusColor}-800`}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {commission.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {new Date(commission.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => {
                              setSelectedCommission(commission);
                              setShowDetailModal(true);
                            }}
                            className="text-blue-600 hover:text-blue-800 font-medium text-sm"
                          >
                            Review
                          </button>
                          <button
                            onClick={() => {
                              setSelectedCommissionForMessaging(commission);
                              setShowMessagingModal(true);
                            }}
                            className={`font-medium text-sm flex items-center ${
                              commission.status === 'needs_reply' || commission.has_unread_messages
                                ? 'text-red-600 hover:text-red-800' 
                                : 'text-gray-600 hover:text-gray-800'
                            }`}
                          >
                            <MessageSquare className="w-4 h-4 mr-1" />
                            {commission.status === 'needs_reply' ? 'Reply' : 'Message'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Commission Detail Modal */}
      {showDetailModal && selectedCommission && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold">Commission Details</h2>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
            </div>
            
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Agent Information */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-medium text-gray-900 mb-3 flex items-center">
                    <User className="w-4 h-4 mr-2" />
                    Agent Information
                  </h3>
                  <div className="space-y-2">
                    <p className="text-sm">
                      <span className="font-medium">Name:</span> {selectedCommission.first_name} {selectedCommission.last_name}
                    </p>
                    <p className="text-sm">
                      <span className="font-medium">Email:</span> {selectedCommission.email}
                    </p>
                    <p className="text-sm">
                      <span className="font-medium">Phone:</span> {selectedCommission.phone}
                    </p>
                  </div>
                </div>

                {/* Guest Information */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-medium text-gray-900 mb-3 flex items-center">
                    <User className="w-4 h-4 mr-2" />
                    Guest Information
                  </h3>
                  <div className="space-y-2">
                    <p className="text-sm">
                      <span className="font-medium">Name:</span> {selectedCommission.guest_first_name} {selectedCommission.guest_last_name}
                    </p>
                    <p className="text-sm">
                      <span className="font-medium">Booking Date:</span> {new Date(selectedCommission.booking_date).toLocaleDateString()}
                    </p>
                    <p className="text-sm">
                      <span className="font-medium">Travel Date:</span> {new Date(selectedCommission.travel_date).toLocaleDateString()}
                    </p>
                  </div>
                </div>

                {/* Booking Details */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-medium text-gray-900 mb-3 flex items-center">
                    <FileText className="w-4 h-4 mr-2" />
                    Booking Details
                  </h3>
                  <div className="space-y-2">
                    <p className="text-sm">
                      <span className="font-medium">Reservation #:</span> {selectedCommission.reservation_number}
                    </p>
                    <p className="text-sm">
                      <span className="font-medium">Supplier:</span> {selectedCommission.supplier}
                    </p>
                    {selectedCommission.itinerary_id && (
                      <p className="text-sm">
                        <span className="font-medium">Itinerary ID:</span> {selectedCommission.itinerary_id}
                      </p>
                    )}
                  </div>
                </div>

                {/* Commission Details */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-medium text-gray-900 mb-3 flex items-center">
                    <DollarSign className="w-4 h-4 mr-2" />
                    Commission Details
                  </h3>
                  <div className="space-y-2">
                    <p className="text-sm">
                      <span className="font-medium">Gross Commission:</span> ${parseFloat(selectedCommission.gross_commission || 0).toFixed(2)}
                    </p>
                    <p className="text-sm">
                      <span className="font-medium">Agent Commission:</span> ${parseFloat(selectedCommission.agent_commission || 0).toFixed(2)}
                    </p>
                    <p className="text-sm">
                      <span className="font-medium">Status:</span> 
                      <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${getStatusColor(selectedCommission.status)}-100 text-${getStatusColor(selectedCommission.status)}-800`}>
                        {selectedCommission.status}
                      </span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Notes Section */}
              {selectedCommission.notes && (
                <div className="mt-6 bg-gray-50 rounded-lg p-4">
                  <h3 className="font-medium text-gray-900 mb-2">Agent Notes</h3>
                  <p className="text-sm text-gray-700">{selectedCommission.notes}</p>
                </div>
              )}

              {/* Admin Notes Section */}
              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Admin Notes
                </label>
                <textarea
                  id="admin-notes"
                  rows={4}
                  defaultValue={selectedCommission.admin_notes || ''}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Add notes about this commission..."
                />
              </div>

              {/* Action Buttons */}
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
                >
                  Cancel
                </button>
                {selectedCommission.status === 'pending' && (
                  <>
                    <button
                      onClick={() => {
                        const notes = document.getElementById('admin-notes').value;
                        handleUpdateStatus(selectedCommission.id, 'denied', notes);
                      }}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center"
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Deny
                    </button>
                    <button
                      onClick={() => {
                        const notes = document.getElementById('admin-notes').value;
                        handleUpdateStatus(selectedCommission.id, 'on_hold', notes);
                      }}
                      className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 flex items-center"
                    >
                      <AlertCircle className="w-4 h-4 mr-2" />
                      Hold
                    </button>
                    <button
                      onClick={() => {
                        const notes = document.getElementById('admin-notes').value;
                        handleUpdateStatus(selectedCommission.id, 'approved', notes);
                      }}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Approve
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {showMessagingModal && selectedCommissionForMessaging && (
        <AdminCommissionMessagingModal 
          commission={selectedCommissionForMessaging} 
          onClose={() => setShowMessagingModal(false)} 
          onMessageSent={handleMessageSent} 
        />
      )}
    </div>
  );
};

export default CommissionsManagement;
